import { useMemo } from 'react';
import ChapterSection from '../ChapterSection/ChapterSection';
import Icon, { type IconName } from '@/components/ui/Icon/Icon';
import OverviewMiniMap from '../OverviewMiniMap/OverviewMiniMap';
import LazyMount from '../OverviewMiniMap/LazyMount';
import type {
  ReinfoLayerSummary,
  ReinfoPortfolio,
  SiteAnalysisResponse,
} from '@/lib/api.types';
import type { MapSpec, MarkerSpec } from '@/lib/map-spec';
import type { LeadSpec } from '@/lib/report-content';
import { TILES } from '@/lib/tiles';
import {
  formatDistanceM,
  formatWalkMinutes,
  formatYen,
  splitAddress,
} from '@/lib/format';
import { ChapterLead } from './shared';
import type { StatusKind } from '@/components/ui/StatusRow/StatusRow';
import styles from './ChapterOverview.module.css';

type Props = {
  analysis: SiteAnalysisResponse;
};

type AmenityDef = {
  endpoint: string;
  icon: IconName;
  color: string;
  label: string;
};

// Endpoint → pin styling. Order = priority when more than 4 layers have a nearest_point.
// コードは backend の LAYER_CONFIGS（不動産情報ライブラリの実コード）に一致させること。
// アイコンは Tabler outline（§5）。絵文字は使わない（§8）。
const AMENITY_DEFS: AmenityDef[] = [
  { endpoint: 'XKT015', icon: 'train', color: '#7AA8C7', label: '最寄駅' },
  { endpoint: 'XKT010', icon: 'stethoscope', color: '#C76D5A', label: '医療機関' },
  { endpoint: 'XKT006', icon: 'school', color: '#D9A441', label: '学校' },
  { endpoint: 'XKT007', icon: 'building-community', color: '#C99A3B', label: '保育園・幼稚園' },
];

// value_label は「名称 / 種別 / 住所…」の連結で、医療機関などは法人名+所在地まで入って
// 非常に長くなる。凡例・ピンのツールチップには先頭の名称だけを使う(詳細は暮らし章の領分)。
function amenityShortLabel(valueLabel: string | null | undefined): string | null {
  if (!valueLabel) return null;
  const head = valueLabel.split(' / ')[0]?.trim();
  return head || null;
}

export type AmenityPin = {
  marker: MarkerSpec;
  icon: IconName;
  color: string;
  label: string;
  distanceLabel: string | null;
};

function pickAmenityPins(
  layers: ReinfoLayerSummary[],
  defs: AmenityDef[],
  limit = 4,
): AmenityPin[] {
  const pins: AmenityPin[] = [];
  for (const def of defs) {
    if (pins.length >= limit) break;
    const layer = layers.find(
      (l) => l.endpoint === def.endpoint && l.nearest_point != null,
    );
    if (!layer || !layer.nearest_point) continue;
    const distance =
      layer.nearest_distance_m != null ? formatDistanceM(layer.nearest_distance_m) : null;
    const labelText = amenityShortLabel(layer.value_label) || def.label;
    const title = distance ? `${labelText} (${distance})` : labelText;
    pins.push({
      icon: def.icon,
      color: def.color,
      label: labelText,
      distanceLabel: distance,
      marker: {
        point: layer.nearest_point,
        icon: def.icon,
        color: def.color,
        title,
      },
    });
  }
  return pins;
}

// ---- 土地のプロフィール（カード4枚 + リード文）のデータ抽出 ----

// 徒歩圏のめやす。不動産表示規約の 80m/分 で 20 分 = 1.6km を超えたら分数を出さない。
const WALK_LIMIT_M = 1600;

function getNumberMetric(
  metrics: Record<string, unknown>,
  key: string,
): number | null {
  const v = metrics[key];
  return typeof v === 'number' ? v : null;
}

function getStringMetric(
  metrics: Record<string, unknown>,
  key: string,
): string | null {
  const v = metrics[key];
  return typeof v === 'string' && v ? v : null;
}

// 直線距離の表記。formatDistanceM と異なり km 帯にも「約」を付ける（めやす表示のため）。
function straightDistanceLabel(meters: number): string {
  if (meters >= 1000) return `約 ${(meters / 1000).toFixed(1)} km`;
  return `約 ${Math.round(meters)} m`;
}

// formatYen は 1万円以上を整数の万円に丸めるため、地方で珍しくない 1万〜10万円/坪 帯では
// 誤差が最大 ±33% になる。見出しの一枚数字になるあらまし章ではこの帯だけ小数1桁で出す。
function formatTsuboYen(yen: number): string {
  if (yen >= 10000 && yen < 100000) return `${(yen / 10000).toFixed(1)}万円`;
  return formatYen(yen);
}

// 取得失敗（API エラー・API キー未設定でスキップ）。not_found と区別し、
// 「見つかりませんでした」と断定しない（見通し章 prospects と同じ規約）。
const FETCH_FAILED: ReadonlySet<string> = new Set(['error', 'skipped']);

type StationInfo =
  | { kind: 'found'; name: string; line: string | null; distanceM: number | null }
  | { kind: 'not_found' }
  | { kind: 'unavailable' };

// 駅名は representative_properties（コード契約で安定）から取る。value_label は
// S12_001_ja が欠けると汎用結合形（事業者名が先頭）に落ちるため、フォールバックは
// 先頭が「駅」で終わるときだけ信用する。
function stationInfo(life: ReinfoPortfolio): StationInfo {
  const layer = life.layers.find((l) => l.endpoint === 'XKT015');
  if (!layer) {
    return FETCH_FAILED.has(life.status)
      ? { kind: 'unavailable' }
      : { kind: 'not_found' };
  }
  if (FETCH_FAILED.has(layer.status)) return { kind: 'unavailable' };
  if (layer.status !== 'success') return { kind: 'not_found' };
  const rawName = layer.representative_properties?.['S12_001_ja'];
  let name = typeof rawName === 'string' && rawName ? `${rawName}駅` : null;
  if (!name && layer.value_label) {
    const head = layer.value_label.split(' / ')[0]?.split('（')[0]?.trim();
    name = head && /駅$/.test(head) ? head : null;
  }
  if (!name) return { kind: 'not_found' };
  const rawLine = layer.representative_properties?.['S12_003_ja'];
  const line = typeof rawLine === 'string' && rawLine ? rawLine : null;
  return { kind: 'found', name, line, distanceM: layer.nearest_distance_m ?? null };
}

// 用途地域 → 日常語の言い換え（§6.3）。「何が建てられる区分か」の事実だけを語り、
// 閑静・好立地などの評価語は使わない（§6.5）。キーは MLIT use_area_ja の正式名称。
const ZONING_GLOSS: Record<string, string> = {
  第一種低層住居専用地域: '低い家がならぶ住宅のまち',
  第二種低層住居専用地域: '低い家と小さなお店がならぶ住宅のまち',
  第一種中高層住居専用地域: 'マンションも建てられる住宅のまち',
  第二種中高層住居専用地域: 'マンションとお店がまじる住宅のまち',
  第一種住居地域: '住宅を中心に、お店や事務所もまじるまち',
  第二種住居地域: '住宅を中心に、お店や娯楽施設もまじるまち',
  準住居地域: '大きな道路ぞいの、住まいとお店がまじるまち',
  田園住居地域: '農地と住宅が寄りそうまち',
  近隣商業地域: '日々の買い物のお店が集まる、住まいもまじるまち',
  商業地域: 'お店やオフィスが集まる、まちの中心',
  準工業地域: '軽めの工場と住宅・お店がまじるまち',
  工業地域: '工場が中心のまち（住宅も建てられます）',
  工業専用地域: '工場のための区域 ── 住宅は建てられません',
};

type TownCharacter = {
  value: string;
  sub?: string;
  badge?: { label: string; kind: StatusKind };
  leadSentence?: string;
  // 調整区域の一文の直後に「まずは地図で確かめて」を続けると最初の一歩の指示が
  // 競合するため、締めの一文を省く。
  omitLeadCloser?: boolean;
};

// バッジは alert（市街化調整区域・工業専用地域）のみ。語は正準 (lib/risk.ts) の
// high 相当「要注意」に合わせる。safe ◯ は付けない — 判定に見える表示は
// VerdictCard の領分。データ欠損にはバッジを付けない（「要確認」は medium の
// アラート語、「判定中」は総括の語彙で、カードの value 文だけで足りる）。
function townCharacter(planning: ReinfoPortfolio): TownCharacter {
  // 取得失敗時は「指定が確認できなかった」という地理的示唆をせず、事実だけ言う。
  if (FETCH_FAILED.has(planning.status)) {
    return { value: '用途地域の情報を取得できませんでした' };
  }
  const useArea = getStringMetric(planning.metrics, 'use_area');
  const areaDivision = getStringMetric(planning.metrics, 'area_division');

  // 市街化調整区域は用途地域の有無にかかわらず最優先で出す（隠れる経路を作らない）。
  if (areaDivision && areaDivision.includes('市街化調整')) {
    return {
      value: '市街化調整区域',
      sub: '原則として家を建てられない区域 ── 建築可否は市町村に確認',
      badge: { label: '要注意 △', kind: 'alert' },
      leadSentence:
        'この土地は市街化調整区域 ── 原則として家を建てられない区域にあたります。建てられるかは市町村の都市計画窓口での確認が前提になります。',
      omitLeadCloser: true,
    };
  }
  if (useArea) {
    const gloss = ZONING_GLOSS[useArea];
    if (useArea === '工業専用地域') {
      return {
        value: useArea,
        sub: gloss,
        badge: { label: '要注意 △', kind: 'alert' },
        leadSentence: 'このあたりは工場のための区域で、住宅は建てられません。',
      };
    }
    if (gloss) {
      // 工業地域の gloss は括弧文を含み「…（住宅も建てられます）です。」と破格になるため
      // リードだけ言い直す。
      const leadSentence =
        useArea === '工業地域'
          ? 'このあたりは工場が中心のまちで、住宅も建てられます。'
          : `このあたりは、${gloss}です。`;
      return { value: useArea, sub: gloss, leadSentence };
    }
    // 将来のデータ揺れ（未知の用途地域名）— 生の名称と正準訳で受ける。
    return {
      value: useArea,
      sub: '何を建ててよいか決められた地域区分です',
    };
  }
  if (areaDivision) {
    return {
      value: areaDivision,
      sub: areaDivision === '市街化区域' ? '市街地として整備される区域' : undefined,
    };
  }
  return {
    value: '用途地域の指定が確認できませんでした',
    sub: '都市計画区域の外か、データの範囲外の可能性があります',
  };
}

type PriceFacts = {
  // 取引中央値は建物付き取引も含み得るため「地価」とは呼ばず「取引価格」と
  // ラベルする（価格章の表記と同じ整理）。地価公示フォールバック時のみ「地価」。
  label: string;
  value: string;
  sub?: string;
};

function priceFacts(price: ReinfoPortfolio): PriceFacts {
  if (FETCH_FAILED.has(price.status)) {
    return {
      label: '価格のめやす',
      value: '価格データを取得できませんでした',
    };
  }
  const txMedian = getNumberMetric(
    price.metrics,
    'median_transaction_price_per_tsubo_yen',
  );
  const publicMedian = getNumberMetric(
    price.metrics,
    'median_public_price_per_tsubo_yen',
  );
  const txSamples = getNumberMetric(price.metrics, 'transaction_samples') ?? 0;

  if (txMedian != null) {
    const sub =
      txSamples === 1
        ? '周辺の取引1件のみ ── めやすとしてご覧ください'
        : txSamples > 1
          ? `周辺の取引${txSamples}件をもとにした中央値（1坪 ≒ 3.3m²）`
          : '周辺の取引をもとにした中央値（1坪 ≒ 3.3m²）';
    return {
      label: '取引価格のめやす',
      value: `坪あたり 約${formatTsuboYen(txMedian)}`,
      sub,
    };
  }
  if (publicMedian != null) {
    return {
      label: '地価のめやす',
      value: `坪あたり 約${formatTsuboYen(publicMedian)}`,
      sub: '地価公示・地価調査の中央値（1坪 ≒ 3.3m²）',
    };
  }
  return {
    label: '価格のめやす',
    value: '参考になる価格データが見つかりませんでした',
    sub: '第三章とあわせてご確認ください',
  };
}

// リード文。データが揃う文だけを順に並べ、締めの一文で地図へ誘導する。
// ハザード・地盤・注意度には触れない — 判定は直上の VerdictCard の領分。
function buildLeadMessage(station: StationInfo, town: TownCharacter): string {
  const sentences: string[] = [];
  if (station.kind === 'found' && station.distanceM != null) {
    const stationLabel = station.line
      ? `${station.name}（${station.line}）`
      : station.name;
    if (station.distanceM <= WALK_LIMIT_M) {
      const minutes = Math.max(1, Math.ceil(station.distanceM / 80));
      sentences.push(`${stationLabel}まで、歩いて約${minutes}分。`);
    } else {
      const km = (station.distanceM / 1000).toFixed(1);
      sentences.push(`最寄りの${stationLabel}までは、直線で約${km}km。`);
    }
  }
  if (town.leadSentence) {
    sentences.push(town.leadSentence);
  }
  if (sentences.length === 0) {
    return 'この土地のまわりを、航空写真と三枚の地図でひと目に。広い視点から順に、土地の置かれた場所を確かめてください。';
  }
  if (!town.omitLeadCloser) {
    sentences.push(
      'まずは航空写真と三枚の地図で、土地とそのまわりをひと目に確かめてください。',
    );
  }
  return sentences.join('');
}

export type StatCardSpec = {
  icon: IconName;
  label: string;
  value: string;
  sub?: string;
  badge?: { label: string; kind: StatusKind };
};

function StatCard({ icon, label, value, sub, badge }: StatCardSpec) {
  const badgeClass = badge
    ? badge.kind === 'safe'
      ? styles.badgeSafe
      : badge.kind === 'alert'
        ? styles.badgeAlert
        : styles.badgeNeutral
    : '';
  return (
    <div className={styles.statCard}>
      <div className={styles.statHead}>
        <Icon name={icon} size={18} />
        <span className={styles.statLabel}>{label}</span>
      </div>
      <div className={styles.statValue}>{value}</div>
      {sub && <div className={styles.statSub}>{sub}</div>}
      {badge && (
        <span className={`${styles.statBadge} ${badgeClass}`}>{badge.label}</span>
      )}
    </div>
  );
}

export type OverviewMini = { spec: MapSpec; caption: string; sub: string };

export type OverviewContent = {
  lead: LeadSpec;
  cards: StatCardSpec[];
  /** 徒歩分数を出したときの注記 (出さないときは null)。 */
  walkNote: string | null;
  hero: { caption: string; spec: MapSpec; pins: AmenityPin[] };
  minis: OverviewMini[];
};

const HERO_HEIGHT = 360;
const MINI_HEIGHT = 180;
const WALK_NOTE =
  '※ 徒歩分数は直線距離 80m/分でのめやすです。実際の道のりではこれより長くなることがあります。';

// あらまし章の中身 (プロフィールカード・リード・航空写真・三枚の地図)。
// 画面 (ChapterOverview) と PDF 冊子が共有する純関数。
export function buildOverviewContent(analysis: SiteAnalysisResponse): OverviewContent {
  const { pref, name } = splitAddress(analysis.address);
  const site = analysis.site;

  const pins = pickAmenityPins(analysis.life_environment.layers, AMENITY_DEFS);
  const station = stationInfo(analysis.life_environment);
  const town = townCharacter(analysis.planning);
  const price = priceFacts(analysis.price);
  const leadMessage = buildLeadMessage(station, town);

  const addressDisplay =
    [pref, name].filter(Boolean).join(' ') || '住所情報がありません';

  const showWalkNote =
    station.kind === 'found' &&
    station.distanceM != null &&
    station.distanceM <= WALK_LIMIT_M;

  const stationValue =
    station.kind === 'found'
      ? station.line
        ? `${station.name}（${station.line}）`
        : station.name
      : station.kind === 'unavailable'
        ? '駅の情報を取得できませんでした'
        : 'この調査の範囲では駅が確認できませんでした';
  const stationSub =
    station.kind === 'found' && station.distanceM != null
      ? station.distanceM <= WALK_LIMIT_M
        ? `${formatWalkMinutes(station.distanceM)}（直線 ${straightDistanceLabel(station.distanceM)}）`
        : `直線 ${straightDistanceLabel(station.distanceM)}（徒歩圏のめやすを超えています）`
      : undefined;

  const cards: StatCardSpec[] = [
    { icon: 'map-pin', label: '住所', value: addressDisplay },
    { icon: 'train', label: '最寄駅', value: stationValue, sub: stationSub },
    {
      icon: 'building-community',
      label: 'まちの性格',
      value: town.value,
      sub: town.sub,
      badge: town.badge,
    },
    { icon: 'currency-yen', label: price.label, value: price.value, sub: price.sub },
  ];

  const base = { point: site, geometries: [], legend: [] };
  return {
    lead: { label: '土地のプロフィール', message: leadMessage },
    cards,
    walkNote: showWalkNote ? WALK_NOTE : null,
    hero: {
      caption: '航空写真でみる',
      spec: {
        ...base,
        key: 'hero',
        tileUrl: TILES.photo,
        zoom: 17,
        height: HERO_HEIGHT,
        markers: pins.map((p) => p.marker),
        fitToMarkers: true,
      },
      pins,
    },
    minis: [
      {
        spec: { ...base, key: 'mini-pale', tileUrl: TILES.pale, zoom: 14, height: MINI_HEIGHT, markers: [] },
        caption: '標準地図',
        sub: '広域 / 川・線路・大きな道',
      },
      {
        spec: { ...base, key: 'mini-photo', tileUrl: TILES.photo, zoom: 18, height: MINI_HEIGHT, markers: [] },
        caption: '航空写真',
        sub: '接近 / 屋根のならびと隣地',
      },
      {
        spec: { ...base, key: 'mini-relief', tileUrl: TILES.relief, zoom: 14, height: MINI_HEIGHT, markers: [] },
        caption: '起伏図',
        sub: '高低差 / 青・緑が低く、黄・茶が高い',
      },
    ],
  };
}

export default function ChapterOverview({ analysis }: Props) {
  const content = useMemo(() => buildOverviewContent(analysis), [analysis]);
  const { hero } = content;

  return (
    <ChapterSection id="overview">
      <ChapterLead {...content.lead} />

      <div className={styles.stripRow}>
        {content.cards.map((card) => (
          <StatCard key={card.label} {...card} />
        ))}
      </div>
      {content.walkNote && <p className={styles.stripNote}>{content.walkNote}</p>}

      <div className={styles.heroMapWrap}>
        <span className={styles.heroCaption}>
          <Icon name="map-pin" size={14} />
          {hero.caption}
        </span>
        <LazyMount placeholderHeight={hero.spec.height}>
          <OverviewMiniMap spec={hero.spec} />
        </LazyMount>
        {hero.pins.length > 0 && (
          <div className={styles.heroLegend}>
            {hero.pins.map((p) => (
              <div key={p.marker.icon} className={styles.legendItem}>
                <Icon name={p.icon} size={14} color={p.color} />
                <span className={styles.legendName}>{p.label}</span>
                {p.distanceLabel && (
                  <span className={styles.legendDistance}>{p.distanceLabel}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.miniGrid}>
        {content.minis.map((mini) => (
          <div key={mini.spec.key} className={styles.miniCell}>
            <LazyMount placeholderHeight={mini.spec.height}>
              <OverviewMiniMap spec={mini.spec} />
            </LazyMount>
            <div className={styles.miniCaption}>
              <span>{mini.caption}</span>
              <span className={styles.miniCaptionSub}>{mini.sub}</span>
            </div>
          </div>
        ))}
      </div>
    </ChapterSection>
  );
}
